<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <div
    class="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)] flex flex-col">
    <!-- Error Message -->
    <div
      v-if="error"
      class="mx-4 mb-4 p-4 bg-red-100 text-red-800 rounded-lg shadow">
      {{ error }}
    </div>

    <!-- Form Container -->
    <div
      class="flex-1 overflow-y-auto px-4 pb-24"
      style="padding-bottom: calc(5rem + env(safe-area-inset-bottom))">
      <form @submit.prevent="handleSubmit" class="space-y-4">
        <!-- Name -->
        <div>
          <label class="block text-sm font-semibold mb-2">
            {{ $t('views.groups.form.name.label') }} *
          </label>
          <input
            v-model="formData.name"
            type="text"
            required
            :placeholder="$t('views.groups.form.name.placeholder')"
            class="w-full p-3 rounded-lg border border-[var(--color-primary-400)] bg-[var(--color-primary-100)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-500)]" />
          <span v-if="validationErrors.name" class="text-red-500 text-xs mt-1">
            {{ validationErrors.name }}
          </span>
        </div>

        <!-- Location -->
        <div>
          <label class="block text-sm font-semibold mb-2">
            {{ $t('views.groups.form.location.label') }}
          </label>
          <input
            v-model="formData.location"
            type="text"
            :placeholder="$t('views.groups.form.location.placeholder')"
            class="w-full p-3 rounded-lg border border-[var(--color-primary-400)] bg-[var(--color-primary-100)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-500)]" />
          <span
            v-if="validationErrors.location"
            class="text-red-500 text-xs mt-1">
            {{ validationErrors.location }}
          </span>
        </div>

        <!-- Description -->
        <div>
          <label class="block text-sm font-semibold mb-2">
            {{ $t('views.groups.form.description.label') }}
          </label>
          <textarea
            v-model="formData.description"
            rows="4"
            :placeholder="$t('views.groups.form.description.placeholder')"
            class="w-full p-3 rounded-lg border border-[var(--color-primary-400)] bg-[var(--color-primary-100)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-500)] resize-none" />
          <span
            v-if="validationErrors.description"
            class="text-red-500 text-xs mt-1">
            {{ validationErrors.description }}
          </span>
        </div>

        <!-- Assign Goats Section -->
        <div class="pt-4 border-t border-[var(--color-primary-400)]">
          <div class="mb-3">
            <h2 class="text-lg font-bold text-[var(--color-text)]">
              {{ $t('views.groups.form.assign.label') }} ({{
                $t('views.groups.form.optional')
              }})
            </h2>
            <p class="text-xs text-[var(--color-text)] opacity-70 mt-1">
              {{ $t('views.groups.form.assign.placeholder') }}
            </p>
          </div>

          <!-- Loading State for Goats -->
          <div
            v-if="goatsLoading"
            class="flex items-center justify-center py-8">
            <div class="text-center">
              <div
                class="w-8 h-8 border-4 border-[var(--color-primary-400)] border-t-[var(--color-primary-900)] rounded-full animate-spin mx-auto mb-2"></div>
              <p class="text-xs text-[var(--color-text)]">Loading goats...</p>
            </div>
          </div>

          <!-- No Unassigned Goats -->
          <div
            v-else-if="unassignedGoats.length === 0"
            class="p-4 bg-[var(--color-primary-100)] rounded-lg border border-[var(--color-primary-400)] text-center">
            <p class="text-sm text-[var(--color-text)] opacity-70">
              {{ $t('views.groups.form.noUnassignedGoats') }}
            </p>
          </div>

          <!-- Goat Selection Cards Grid -->
          <div v-else>
            <div class="mb-2 flex justify-between items-center">
              <span class="text-sm text-[var(--color-text)] opacity-70">
                {{ selectedGoatIds.size }}
                {{ $t('views.groups.form.goatsSelected') }}
              </span>
              <button
                v-if="selectedGoatIds.size > 0"
                type="button"
                @click="clearSelection"
                class="text-xs text-blue-600 hover:text-blue-700 underline">
                {{ $t('views.groups.form.clearSelection') }}
              </button>
            </div>

            <div class="grid grid-cols-2 gap-3 max-h-96 overflow-y-auto p-1">
              <GoatSelectionCard
                v-for="goat in unassignedGoats"
                :key="goat.id"
                :goat="goat"
                v-model="selectedGoats[goat.id]" />
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex gap-3 pt-4">
          <button
            type="button"
            @click="handleCancel"
            class="flex-1 p-4 rounded-lg border border-[var(--color-primary-400)] bg-[var(--color-primary-100)] text-[var(--color-text)] hover:bg-[var(--color-primary-200)] transition-colors duration-200 font-semibold">
            {{ $t('common.buttons.cancel') }}
          </button>

          <button
            type="submit"
            :disabled="loading"
            class="flex-1 p-4 rounded-lg border border-[var(--color-primary-400)] bg-[var(--color-primary-200)] text-[var(--color-primary-900)] hover:bg-[var(--color-primary-300)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 font-semibold">
            {{
              loading
                ? $t('views.groups.form.saving')
                : $t('views.groups.form.submit')
            }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted } from 'vue'
  import { useRouter } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import { useGroupStore } from '@/stores/group.store.js'
  import { useGoatStore } from '@/stores/goat.store.js'
  import { useUiStore } from '@/stores/ui.store.js'
  import { storeToRefs } from 'pinia'
  import BackButton from '@/components/ui/buttons/GetBackButton.vue'
  import GoatSelectionCard from '@/components/groups/GoatSelectionCard.vue'

  const router = useRouter()
  const { t } = useI18n()
  const groupStore = useGroupStore()
  const goatStore = useGoatStore()
  const uiStore = useUiStore()

  // Get reactive state from stores
  const { loading, error: storeError } = storeToRefs(groupStore)
  const { unassignedGoats, loading: goatsLoading } = storeToRefs(goatStore)
  const { addGroup, clearError } = groupStore
  const { fetchGoats, updateGoat } = goatStore
  const { queueToast } = uiStore

  // Local state
  const formData = ref({
    name: '',
    location: '',
    description: '',
  })

  const validationErrors = ref({})
  const error = ref(null)

  // Goat selection state
  const selectedGoats = ref({})

  // Computed property for selected goat IDs
  const selectedGoatIds = computed(() => {
    return new Set(
      Object.entries(selectedGoats.value)
        .filter(([_, isSelected]) => isSelected)
        .map(([id, _]) => parseInt(id))
    )
  })

  // ==========================================
  // LIFECYCLE: FETCH UNASSIGNED GOATS
  // ==========================================

  onMounted(async () => {
    //Header
    uiStore.setHeader({
      title: t('views.groups.form.title'), 
      showBack: true
    })
    console.log('NewGroupView mounted')

    // Fetch goats to get unassigned ones
    if (unassignedGoats.value.length === 0) {
      console.log('Fetching goats...')
      await fetchGoats()
    }

    console.log(`Found ${unassignedGoats.value.length} unassigned goats`)
  })

 

  /**
   * Validate form data
   */
  function validateForm() {
    validationErrors.value = {}
    let isValid = true

    // Name validation (required)
    if (!formData.value.name || formData.value.name.trim().length < 2) {
      validationErrors.value.name = t('views.groups.form.nameRequired')
      isValid = false
    }

    // Location validation (optional, but if provided must be at least 2 chars)
    if (
      formData.value.location &&
      formData.value.location.trim().length > 0 &&
      formData.value.location.trim().length < 2
    ) {
      validationErrors.value.location = t(
        'views.groups.form.location.minLength'
      )
      isValid = false
    }

    // Description validation (optional, but if provided must be at least 2 chars)
    if (
      formData.value.description &&
      formData.value.description.trim().length > 0 &&
      formData.value.description.trim().length < 2
    ) {
      validationErrors.value.description = t(
        'views.groups.form.description.minLength'
      )
      isValid = false
    }

    return isValid
  }

  /**
   * Clear goat selection
   */
  function clearSelection() {
    selectedGoats.value = {}
    console.log('Cleared goat selection')
  }

  /**
   * Assign selected goats to the group
   * @param {number} groupId - The ID of the newly created group
   */
  async function assignGoatsToGroup(groupId) {
    const goatIds = Array.from(selectedGoatIds.value)

    if (goatIds.length === 0) {
      console.log('No goats selected, skipping assignment')
      return true
    }

    console.log(`Assigning ${goatIds.length} goat(s) to group ${groupId}...`)

    try {
      // Update each selected goat with the group ID
      const updatePromises = goatIds.map((goatId) => {
        const goat = unassignedGoats.value.find((g) => g.id === goatId)
        if (goat) {
          return updateGoat(goatId, {
            ...goat,
            groupId: groupId,
          })
        }
        return Promise.resolve(false)
      })

      const results = await Promise.all(updatePromises)
      const successCount = results.filter((r) => r === true).length

      console.log(
        `Successfully assigned ${successCount}/${goatIds.length} goat(s) to group`
      )
      return successCount === goatIds.length
    } catch (err) {
      console.error('Failed to assign goats to group:', err)
      return false
    }
  }

  /**
   * Handle form submission
   */
  async function handleSubmit() {
    console.log('Form submission started')

    // Clear previous messages
    error.value = null
    clearError()

    // Validate form
    if (!validateForm()) {
      console.log('Form validation failed:', validationErrors.value)
      return
    }

    console.log('Form validation passed')
    console.log('Saving group to database...')

    try {
      // Save group through Pinia store (backend integration)
      const groupId = await addGroup(formData.value)

      if (groupId) {
        console.log('Group saved successfully with ID:', groupId)

        // Assign selected goats to the group
        const assignmentSuccess = await assignGoatsToGroup(groupId)

        if (!assignmentSuccess) {
          console.warn('Some goats may not have been assigned to the group')
        }

        // Queue toast notification to be shown on the destination view
        const toastMessage =
          selectedGoatIds.value.size > 0
            ? t('views.groups.form.successWithGoats', {
                count: selectedGoatIds.value.size,
              })
            : t('views.groups.form.success')

        queueToast(toastMessage, 'saved')

        // Navigate immediately without delay
        router.push('/groups')
      } else {
        error.value = storeError.value || t('views.groups.form.error')
      }
    } catch (err) {
      console.error('Failed to save group:', err)
      error.value = t('views.groups.form.error')
    }
  }

  /**
   * Handle cancel button
   */
  function handleCancel() {
    router.back()
  }
</script>
